import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Heading, Modal, Division, Form, FormInstance, Icon, Icons, useToast, Breadcrumbs, Flex, Spin, EmptyState } from "@ir/ui";
import { BackupRequestDataForSubmit, BackupRequestDotFoodsFormValues, BackupRequestKeheFormValues, BackupRequestModalProps } from "./BackupRequestInterface";
import BackupRequestDotFoodsForm from "./BackupRequestDotFoodsForm";
import BackupRequestKeheForm from "./BackupRequestKeheForm";
import useDeductionIncomplete from "../hook/useDeductionIncomplete";
import { BackupRequestDTO } from "@/core/dto/deduction/remittance";
import { useConfirm, useEventBusSubscriber } from "@/hooks";
import { BackupRequestEvents, BackupRequestStatusUpdatePayload } from "@/core/events/backupRequestEventsMap";
import "./BackupRequest.scss";
import { useDeductionDrawer, useRemittanceItem } from "@/presentation/deduction/hooks";
import { createLogger } from "@/infrastructure/services/logger";

const BackupRequestModal = ({ isOpen, onClose, purchaserKey }: BackupRequestModalProps) => {
  const logger = createLogger("BackupRequestModal");
  const [dotFoodsForm] = Form.useForm<BackupRequestDotFoodsFormValues>();
  const [keheForm] = Form.useForm<BackupRequestKeheFormValues>();
  const [isRequestingBackup, setIsRequestingBackup] = useState(false);
  const { drawerInfo } = useDeductionDrawer();
  const [isWaitingForBackupRequestStatus, setIsWaitingForBackupRequestStatus] = useState(false);
  const { raiseBackupRequest } = useDeductionIncomplete();
  const { error: errorToast, success: successToast, warning: warningToast } = useToast();
  const confirm = useConfirm();

  const { handleUpdateRemittanceItem } = useRemittanceItem();

  const handleCloseModal = useCallback(() => {
    dotFoodsForm?.resetFields();
    keheForm?.resetFields();
    onClose();
    setIsWaitingForBackupRequestStatus(false);
  }, [dotFoodsForm, keheForm, onClose]);

  useEventBusSubscriber(BackupRequestEvents.BACKUP_REQUEST_STATUS_UPDATE, (payload: BackupRequestStatusUpdatePayload) => {
    const remittanceItem = drawerInfo?.remittanceItem;

    if (remittanceItem && payload.isBackupRequested) {
      remittanceItem.isBackupRequested = payload.isBackupRequested;
      handleUpdateRemittanceItem(remittanceItem);
    }

    if (payload.isBackupRequested) {
      handleCloseModal();
      successToast({
        content: payload.message,
        icon: <Icon name={Icons.CheckCircle} />,
      });
    } else {
      setIsWaitingForBackupRequestStatus(false);
      errorToast({
        content: "Failed to request backup",
        icon: <Icon name={Icons.AlertCircle} />,
      });
    }
  });

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (isWaitingForBackupRequestStatus && isOpen) {
      timeout = setTimeout(() => {
        warningToast({
          content: "Could not retrieve the status of this operation. Please check back after sometime.",
          icon: <Icon name={Icons.AlertCircle} />,
          duration: 10 * 1000, // 10 seconds
        });
        handleCloseModal();
      }, 30 * 1000); // 30 seconds
    }
    return () => clearTimeout(timeout);
  }, [isWaitingForBackupRequestStatus, isOpen, warningToast, handleCloseModal]);

  const handleBackupRequestSubmit = async (backupSubmitData: BackupRequestDataForSubmit) => {
    // confirmation modal
    const confirmResult = await confirm({
      title: "Confirm Submission",
      header: (
        <Division className="confirm-dialog-header">
          <Breadcrumbs items={[{ label: `Check #${drawerInfo?.checkNumber}` }, { label: `${drawerInfo?.invoiceNumber}` }]} />
        </Division>
      ),
      message: "We are about to submit a backup request. Please ensure all details are correct before proceeding.",
      confirmText: "Submit Request for Backup",
      confirmButtonVariant: "solid",
      confirmButtonColor: "primary",
      cancelText: "Back",
      cancelButtonVariant: "outline",
      cancelButtonColor: "primary",
    });

    if (!confirmResult) {
      return;
    }

    let response;
    try {
      setIsRequestingBackup(true);
      setIsWaitingForBackupRequestStatus(true);
      response = await raiseBackupRequest(new BackupRequestDTO(backupSubmitData));

      if (response?.backupRequest?.id) {
        setIsRequestingBackup(false);
      }
    } catch (error) {
      logger.error("Error requesting backup", error);
      errorToast({
        content: "Failed to request backup",
        icon: <Icon name={Icons.AlertCircle} />,
      });
      setIsWaitingForBackupRequestStatus(false);
    } finally {
      setIsRequestingBackup(false);
    }
  };

  const getFormByPurchaserKey = (purchaserKey: string) => {
    switch (purchaserKey) {
      case "DOT_FOODS":
        return <BackupRequestDotFoodsForm backupRequestForm={dotFoodsForm} onBackupRequestSubmit={handleBackupRequestSubmit} />;

      case "KEHE":
      case "SUPERVALU":
        return <BackupRequestKeheForm backupRequestForm={keheForm} onBackupRequestSubmit={handleBackupRequestSubmit} />;

      default:
        return (
          <EmptyState
            className="bg-white px-4 py-5 m-2 w-100 mb-4"
            icon={Icons.Settings}
            title={
              <Heading level={6} className="text-grey-700 font-bold mt-3">
                You are not authorized to request backup for this purchaser.
              </Heading>
            }
          />
        );
    }
  };

  const getHeaderContent = () => {
    return (
      <Flex direction="column" gap={0.25}>
        <Heading level={6} className="dispute-modal-heading">
          Request Backup
        </Heading>
        <Breadcrumbs items={[{ label: `Check #${drawerInfo?.checkNumber}` }, { label: `${drawerInfo?.invoiceNumber}` }]} />
      </Flex>
    );
  };

  const getFooterContent = () => {
    let currentForm: FormInstance;

    switch (purchaserKey) {
      case "DOT_FOODS":
        currentForm = dotFoodsForm;
        break;

      case "KEHE":
      case "SUPERVALU":
        currentForm = keheForm;
        break;

      default:
        return null;
    }
    return (
      <Division className="d-flex justify-content-end pt-3 pb-1">
        <Button onPress={handleCloseModal} variant="outline" color="grey">
          Cancel
        </Button>
        <Button color="primary" onPress={() => currentForm.submit()}>
          Continue
        </Button>
      </Division>
    );
  };

  const isFormAuthorized = useMemo(() => {
    return purchaserKey === "DOT_FOODS" || purchaserKey === "KEHE" || purchaserKey === "SUPERVALU";
  }, [purchaserKey]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCloseModal}
      zIndex={999}
      headerContent={getHeaderContent()}
      footerContent={getFooterContent()}
      hideFooter={isWaitingForBackupRequestStatus || !isFormAuthorized}
      wrapClassName="upload-remittance-modal"
      width={isWaitingForBackupRequestStatus || !isFormAuthorized ? "480px" : "960px"}
    >
      {isWaitingForBackupRequestStatus ? (
        <Flex align="center" justifyContent="center" style={{ minHeight: "200px", height: "100%" }}>
          <Spin spinning={isWaitingForBackupRequestStatus} tip={isRequestingBackup ? "Submitting..." : "Requesting..."} size="large" />
        </Flex>
      ) : (
        <Division style={{ display: isWaitingForBackupRequestStatus ? "none" : "flex" }}>{getFormByPurchaserKey(purchaserKey)}</Division>
      )}
    </Modal>
  );
};

export default BackupRequestModal;
